import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Order } from "@apis/index";
import { toast } from "react-toastify";
import Typography from "@components/typography";
import StoreSelector from "@commons/pos/store-selector";
import ProductGrid from "@commons/pos/product-grid";
import CustomerSearch from "@commons/pos/customer-search";
import Cart from "@commons/pos/cart";
import * as S from "@commons/pos/style";

const AdminPOS = () => {
  const navigate = useNavigate();
  const [storeId, setStoreId] = useState<number | null>(null);
  const [customer, setCustomer] = useState<any>(null);
  const [cart, setCart] = useState<any[]>([]);
  const [deliveryMode, setDeliveryMode] = useState<"pickup" | "delivery">(
    "pickup",
  );
  const [deliveryManId, setDeliveryManId] = useState<number | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<"Paid" | "Unpaid">("Paid");
  const [discountType, setDiscountType] = useState<"flat" | "percentage">(
    "flat",
  );
  const [discountValue, setDiscountValue] = useState("");
  const [loading, setLoading] = useState(false);

  const getDiscountAmount = (subtotal: number) => {
    const numericDiscount = Math.max(0, Number(discountValue) || 0);
    return discountType === "percentage"
      ? Math.min(subtotal, (subtotal * Math.min(numericDiscount, 100)) / 100)
      : Math.min(subtotal, numericDiscount);
  };

  const handleAddToCart = (product: any) => {
    const existing = cart.find((item) => item.id === product.id);
    if (existing) {
      setCart(
        cart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        ),
      );
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
  };

  const handleCheckout = async () => {
    if (!storeId || !customer || cart.length === 0) {
      toast.warn("Please select a store, customer, and add items to cart.");
      return;
    }

    if (deliveryMode === "delivery" && !deliveryManId) {
      toast.warn("Please select a delivery man for delivery orders.");
      return;
    }

    try {
      setLoading(true);
      const totalAmount = cart.reduce(
        (sum, item) => sum + item.price * item.quantity,
        0,
      );
      const discountAmount = getDiscountAmount(totalAmount);
      const actualAmount = Math.max(0, totalAmount - discountAmount);

      const payload = {
        totalAmount,
        actualAmount,
        discountAmount,
        paidAmount: paymentStatus === "Paid" ? actualAmount : 0,
        dueAmount: paymentStatus === "Paid" ? 0 : actualAmount,
        createdBy: customer.id,
        storeId,
        items: cart.map((item) => ({
          quantity: item.quantity,
          productId: item.id,
          totalAmount: item.price * item.quantity,
          actualAmount: item.price * item.quantity,
          discountAmount: 0,
        })),
        shippingInfos: {
          type: deliveryMode,
          description:
            deliveryMode === "pickup" ? "POS Pickup" : "POS Delivery",
        },
        source: "POS",
      };

      const res = await Order.createPOS(payload);

      if (res) {
        const orderId = res.id;

        // Determine initial status based on delivery mode
        let initialStatus =
          deliveryMode === "pickup" ? "Delivered" : "Order Confirmed";

        if (deliveryMode === "delivery" && deliveryManId) {
          initialStatus = "Delivery Assigned";
        }

        await Order.updateStatus(orderId, initialStatus, {
          processingTime: "0",
          paidAmount: paymentStatus === "Paid" ? actualAmount : 0,
          dueAmount: paymentStatus === "Paid" ? 0 : actualAmount,
        });

        // Handle Delivery assignment if needed
        if (deliveryMode === "delivery" && deliveryManId) {
          await Order.assignDelivery(orderId, deliveryManId);
        }

        toast.success(
          `Order #${orderId} ${deliveryMode === "pickup" ? "completed" : "placed"} successfully!`,
        );

        // Reset POS state
        setCart([]);
        setCustomer(null);
        setDeliveryManId(null);
        setPaymentStatus("Paid");
        setDiscountType("flat");
        setDiscountValue("");
        navigate("/admin/orders/delivered");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to place order.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <S.POSContainer>
      <S.Header>
        <div>
          <Typography styleText="font-size: 24px; font-weight: 600">
            Point of Sale (POS)
          </Typography>
          <Typography styleText="color: #6B7280; margin-top: 4px;">
            Create walk-in or manual orders
          </Typography>
        </div>
      </S.Header>

      <S.TwoColumnLayout>
        <S.LeftPanel>
          <StoreSelector onStoreSelect={setStoreId} selectedStoreId={storeId} />

          <CustomerSearch
            onCustomerSelect={setCustomer}
            selectedCustomer={customer}
          />

          <ProductGrid
            storeId={storeId || undefined}
            onAddToCart={handleAddToCart}
          />
        </S.LeftPanel>

        <S.RightPanel>
          <Cart
            cart={cart}
            setCart={setCart}
            deliveryMode={deliveryMode}
            setDeliveryMode={setDeliveryMode}
            deliveryManId={deliveryManId}
            setDeliveryManId={setDeliveryManId}
            paymentStatus={paymentStatus}
            setPaymentStatus={setPaymentStatus}
            discountType={discountType}
            setDiscountType={setDiscountType}
            discountValue={discountValue}
            setDiscountValue={setDiscountValue}
            onCheckout={handleCheckout}
            loading={loading}
            canCheckout={!!storeId && !!customer && cart.length > 0}
          />
        </S.RightPanel>
      </S.TwoColumnLayout>
    </S.POSContainer>
  );
};

export default AdminPOS;
